import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { MagnifyingGlass, ArrowLeft, ShieldCheck, Wrench, CheckCircle, Warning } from "@phosphor-icons/react";
import { dmY } from "@/lib/dateFmt";

const fmtWhen = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    const p = (n) => String(n).padStart(2, "0");
    return `${p(d.getDate())}/${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

/**
 * v1.27.33 — Trace UTR super-admin diagnostic. Paste any UTR and see every
 * upload + transaction that references it, with a per-row diagnosis. Orphans
 * (verified upload with no matching payment txn) get a one-click Reingest
 * button that rebuilds the missing ledger row using the same logic as the
 * original verify flow.
 */
export default function TraceUtr() {
    const navigate = useNavigate();
    const [utr, setUtr] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [fixingId, setFixingId] = useState(null);
    const [bulkBusy, setBulkBusy] = useState(false);
    // v1.27.36 — Recent reingests audit strip
    const [recent, setRecent] = useState([]);

    const loadRecent = async () => {
        try {
            const { data } = await api.get("/utr-reingest-log", { params: { limit: 8 } });
            setRecent(data.rows || []);
        } catch (e) { /* silent — strip is informational */ }
    };

    React.useEffect(() => { loadRecent(); }, []);

    const runTrace = async () => {
        const q = utr.trim();
        if (q.length < 4) { toast.error("Enter at least 4 characters of the UTR"); return; }
        setBusy(true);
        try {
            const { data } = await api.get("/utr-trace", { params: { utr: q } });
            setResult(data);
            if (data.counts.uploads === 0 && data.counts.transactions === 0) {
                toast.info(`No trace found for '${q}'`);
            }
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const fixOne = async (upload_id) => {
        setFixingId(upload_id);
        try {
            const { data } = await api.post(`/utr-reingest/${upload_id}`);
            if (data.reingested) toast.success(`Rebuilt ledger row · ₹${data.amount?.toLocaleString("en-IN")}`);
            else toast.info(data.reason || "Nothing to reingest");
            await runTrace();
            await loadRecent();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setFixingId(null); }
    };

    const bulkFix = async () => {
        if (!window.confirm("Scan EVERY verified upload in the current tenant and rebuild the missing ledger row for each orphan. This does not touch already-fixed rows. Continue?")) return;
        setBulkBusy(true);
        try {
            const { data } = await api.post("/utr-reingest-all-orphans");
            toast.success(`Scanned ${data.scanned} · Fixed ${data.fixed}`);
            if (utr.trim().length >= 4) await runTrace();
            await loadRecent();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBulkBusy(false); }
    };

    const statusPill = (s) => {
        const cls = s === "verified" ? "bg-emerald-100 text-emerald-800"
            : s === "rejected" ? "bg-rose-100 text-rose-800"
            : "bg-amber-100 text-amber-800";
        return <span className={`inline-flex items-center rounded-sm px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${cls}`}>{s || "?"}</span>;
    };

    return (
        <div className="min-h-screen bg-slate-50 px-4 py-6 sm:px-8" data-testid="trace-utr-page">
            <div className="mx-auto max-w-5xl">
                <button
                    type="button"
                    onClick={() => navigate("/admin/super")}
                    data-testid="trace-utr-back"
                    className="mb-3 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-700"
                >
                    <ArrowLeft size={12} /> Super Dashboard
                </button>
                <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.25em] text-amber-700">
                            <ShieldCheck size={14} weight="fill" /> Super Diagnostic
                        </div>
                        <h1 className="mt-1 font-display text-3xl font-bold text-slate-900 sm:text-4xl">Trace UTR</h1>
                        <p className="mt-2 text-sm text-slate-500">
                            Paste any UTR to see every upload and transaction that references it, across all parties and companies you can access. Orphans (verified upload but missing ledger row) get a one-click fix.
                        </p>
                    </div>
                    <Button
                        data-testid="trace-utr-bulk-btn"
                        onClick={bulkFix}
                        disabled={bulkBusy}
                        className={
                            bulkBusy
                                ? "bg-amber-600 text-white shadow-md hover:bg-amber-700 disabled:opacity-100"
                                : "border border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100"
                        }
                    >
                        <Wrench size={14} className={`mr-1.5 ${bulkBusy ? "animate-spin" : ""}`} />
                        {bulkBusy ? "Scanning…" : "Fix ALL orphans in tenant"}
                    </Button>
                </div>

                <div className="mb-6 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="flex flex-wrap items-end gap-3">
                        <div className="flex-1 min-w-[260px]">
                            <label className="text-[10px] uppercase tracking-widest text-slate-500">UTR to trace</label>
                            <Input
                                data-testid="trace-utr-input"
                                value={utr}
                                onChange={(e) => setUtr(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && runTrace()}
                                placeholder="e.g. 609493718068"
                                className="mt-1 font-mono text-sm"
                                autoFocus
                            />
                        </div>
                        <Button
                            data-testid="trace-utr-run-btn"
                            onClick={runTrace}
                            disabled={busy}
                            className="bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
                        >
                            <MagnifyingGlass size={14} className="mr-1.5" />
                            {busy ? "Tracing…" : "Trace"}
                        </Button>
                    </div>
                </div>

                {/* v1.27.36 — Recent reingests audit strip. Shows the last few
                    payments auto-created by Reingest / Fix ALL / verify-guard. */}
                {recent.length > 0 && (
                    <div data-testid="trace-recent-strip" className="mb-6 rounded-lg border border-emerald-200 bg-emerald-50/50 p-3 shadow-sm">
                        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-emerald-800">
                            <CheckCircle size={12} weight="fill" /> Recently reingested · last {recent.length}
                        </div>
                        <ul className="mt-2 space-y-1 max-h-40 overflow-y-auto text-xs">
                            {recent.map((r) => (
                                <li key={r.id} data-testid={`trace-recent-${r.id}`} className="grid grid-cols-12 gap-2 items-center rounded px-2 py-1 hover:bg-white">
                                    <span className="col-span-3 font-mono text-slate-500">{r.created_at ? fmtWhen(r.created_at) : "—"}</span>
                                    <span className="col-span-3 font-mono text-slate-800">{r.utr || "—"}</span>
                                    <span className="col-span-2 truncate text-slate-600" title={r.company_name}>{r.company_name}</span>
                                    <span className="col-span-2 truncate font-medium text-slate-900" title={r.party_name}>{r.party_name}</span>
                                    <span className="col-span-2 text-right font-mono tabular-nums text-slate-800">₹{Number(r.amount || 0).toLocaleString("en-IN")}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {result && (
                    <div className="space-y-6">
                        <div className="grid grid-cols-3 gap-3 text-xs">
                            <div className="rounded border border-slate-200 bg-white p-3">
                                <div className="text-[10px] uppercase tracking-wider text-slate-500">Uploads found</div>
                                <div data-testid="trace-count-uploads" className="mt-0.5 font-mono text-2xl font-bold text-slate-900">{result.counts.uploads}</div>
                            </div>
                            <div className="rounded border border-slate-200 bg-white p-3">
                                <div className="text-[10px] uppercase tracking-wider text-slate-500">Ledger rows</div>
                                <div data-testid="trace-count-txns" className="mt-0.5 font-mono text-2xl font-bold text-slate-900">{result.counts.transactions}</div>
                            </div>
                            <div className={`rounded border p-3 ${result.counts.orphans > 0 ? "border-amber-300 bg-amber-50" : "border-emerald-200 bg-emerald-50"}`}>
                                <div className={`text-[10px] uppercase tracking-wider ${result.counts.orphans > 0 ? "text-amber-700" : "text-emerald-700"}`}>Orphans</div>
                                <div data-testid="trace-count-orphans" className={`mt-0.5 font-mono text-2xl font-bold ${result.counts.orphans > 0 ? "text-amber-800" : "text-emerald-800"}`}>{result.counts.orphans}</div>
                            </div>
                        </div>

                        <section className="rounded-lg border border-slate-200 bg-white shadow-sm">
                            <div className="border-b border-slate-100 px-4 py-3 flex items-center justify-between">
                                <div className="font-display text-sm font-semibold text-slate-900">Upload records ({result.uploads.length})</div>
                                {result.scope === "all_tenants" && (
                                    <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Cross-tenant view</span>
                                )}
                            </div>
                            {result.uploads.length === 0 ? (
                                <div className="p-6 text-center text-sm text-slate-500">No upload record for this UTR.</div>
                            ) : (
                                <table className="w-full text-sm">
                                    <thead className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500">
                                        <tr>
                                            <th className="px-3 py-2">Date</th>
                                            {result.scope === "all_tenants" && <th className="px-3 py-2">Company</th>}
                                            <th className="px-3 py-2">Party</th>
                                            <th className="px-3 py-2 text-right">Amount</th>
                                            <th className="px-3 py-2">Mode</th>
                                            <th className="px-3 py-2">Status</th>
                                            <th className="px-3 py-2">Diagnosis</th>
                                            <th className="px-3 py-2"></th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {result.uploads.map((u) => (
                                            <tr key={u.id} data-testid={`trace-upload-row-${u.id}`}>
                                                <td className="px-3 py-2 font-mono text-xs">{u.payment_date ? dmY(u.payment_date) : "-"}</td>
                                                {result.scope === "all_tenants" && (
                                                    <td className="px-3 py-2 text-xs text-slate-700">{u.company_name || "—"}</td>
                                                )}
                                                <td className="px-3 py-2 font-medium text-slate-900">{u.party_name}</td>
                                                <td className="px-3 py-2 text-right font-mono tabular-nums">₹{Number(u.amount || 0).toLocaleString("en-IN")}</td>
                                                <td className="px-3 py-2 text-xs text-slate-600">{u.payment_app || "-"}</td>
                                                <td className="px-3 py-2">{statusPill(u.status)}</td>
                                                <td className="px-3 py-2 text-xs">
                                                    {u.needs_reingest ? (
                                                        <span className="inline-flex items-center gap-1 text-amber-800">
                                                            <Warning size={12} weight="fill" /> {u.diagnosis}
                                                        </span>
                                                    ) : (
                                                        <span className="text-slate-600">{u.diagnosis}</span>
                                                    )}
                                                </td>
                                                <td className="px-3 py-2 text-right">
                                                    {u.needs_reingest && (
                                                        <Button
                                                            data-testid={`trace-fix-btn-${u.id}`}
                                                            onClick={() => fixOne(u.id)}
                                                            disabled={fixingId === u.id}
                                                            size="sm"
                                                            className="bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-60"
                                                        >
                                                            <Wrench size={12} className="mr-1" />
                                                            {fixingId === u.id ? "Fixing…" : "Reingest"}
                                                        </Button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </section>

                        <section className="rounded-lg border border-slate-200 bg-white shadow-sm">
                            <div className="border-b border-slate-100 px-4 py-3 font-display text-sm font-semibold text-slate-900">Ledger transactions ({result.transactions.length})</div>
                            {result.transactions.length === 0 ? (
                                <div className="p-6 text-center text-sm text-slate-500 flex flex-col items-center gap-2">
                                    <Warning size={22} className="text-amber-500" weight="fill" />
                                    <span>No ledger transaction found for this UTR. If an upload is listed above with status <b>verified</b>, use the <b>Reingest</b> button on that row to rebuild the missing ledger side.</span>
                                </div>
                            ) : (
                                <table className="w-full text-sm">
                                    <thead className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500">
                                        <tr>
                                            <th className="px-3 py-2">Date</th>
                                            <th className="px-3 py-2">Party</th>
                                            <th className="px-3 py-2">Type</th>
                                            <th className="px-3 py-2 text-right">Amount</th>
                                            <th className="px-3 py-2">Reference</th>
                                            <th className="px-3 py-2">Description</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {result.transactions.map((t) => (
                                            <tr key={t.id} data-testid={`trace-txn-row-${t.id}`}>
                                                <td className="px-3 py-2 font-mono text-xs">{t.date ? dmY(t.date) : "-"}</td>
                                                <td className="px-3 py-2 font-medium text-slate-900">{t.party_name}</td>
                                                <td className="px-3 py-2">
                                                    <span className={`inline-flex items-center rounded-sm px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${t.txn_type === "payment" ? "bg-emerald-100 text-emerald-800" : t.txn_type === "payout" ? "bg-violet-100 text-violet-800" : "bg-slate-100 text-slate-700"}`}>{t.txn_type}</span>
                                                </td>
                                                <td className="px-3 py-2 text-right font-mono tabular-nums">₹{Number(t.amount || 0).toLocaleString("en-IN")}</td>
                                                <td className="px-3 py-2 font-mono text-xs">{t.reference || "-"}</td>
                                                <td className="px-3 py-2 text-xs text-slate-600 max-w-md truncate" title={t.description}>{t.description || ""}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </section>

                        {result.counts.orphans === 0 && result.counts.uploads > 0 && result.counts.transactions > 0 && (
                            <div className="rounded border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 flex items-center gap-2">
                                <CheckCircle size={18} weight="fill" /> Ledger is clean for this UTR — every verified upload has its matching ledger row.
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
